import React from 'react';
import { Loader } from '@getro/rombo';
import toast from 'react-hot-toast';
import { Box, Link, Text } from 'rebass/styled-components';
import strings from 'strings';

import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import usePageHook from '../../../hooks/usePageHook';
import { useGetTalentNetworkSettingsQuery, useUpdateTalentNetworkMutation } from '../../../services/network';
import { useGetJobBoardSettingsQuery, useUpdateJobBoardSettingsMutation } from '../../../services/networkV2';
import TalentNetworkSettings from './talentNetworkSettings';
import { TalentDivider } from './talentDivider';
import { TalentSetting } from './components/talentSetting';

const pageTitle = 'Talent network';

export const JobBoardTalentNetwork = () => {
  const { data, isLoading, isError } = useGetTalentNetworkSettingsQuery();
  const {
    data: jobBoardSettings,
    isLoading: isLoadingJobBoardSettings,
    isError: isErrorJobBoardSettings,
  } = useGetJobBoardSettingsQuery();
  const [updateTalentNetwork, { isLoading: isUpdatingTalentNetwork }] = useUpdateTalentNetworkMutation();
  const [updateJobBoardSettings, { isLoading: isUpdatingJobBoardSettings }] = useUpdateJobBoardSettingsMutation();

  usePageHook(pageTitle);

  const getTabsOrder = () => {
    if (!jobBoardSettings?.tabsOrder) return [];
    return jobBoardSettings.tabsOrder.split(',').map((item) => item.trim());
  };

  const onChangeTalentNetwork = () => {
    updateTalentNetwork({ tnHidden: !data.tnHidden }).then(() => {
      const tabsOrder = getTabsOrder();

      toast('Changes saved successfully', { icon: 'success' });

      if (data.tnHidden) {
        updateJobBoardSettings({ tabsOrder: [...new Set([...tabsOrder, 'talent_network'])].join(', ') });
      } else {
        updateJobBoardSettings({ tabsOrder: tabsOrder.filter((item) => item !== 'talent_network').join(', ') });
      }
    });
  };

  if (isLoading || isLoadingJobBoardSettings) {
    return <Loader />;
  }

  return (
    <Box maxWidth="600px">
      <SettingsLayoutHeader
        title="Talent network"
        description={
          <Text>
            Let job board visitors express interest in working with your companies and grow your candidate pool.{' '}
            <Link
              href="https://help.getro.com/support/solutions/articles/65000184610-using-the-getro-talent-network"
              target="_blank"
              rel="noopener noreferrer"
              color="text.main"
            >
              Learn more
            </Link>
            .
          </Text>
        }
      />
      <Box pt="32px">
        <TalentSetting
          toggleTalentNetwork={onChangeTalentNetwork}
          tnHidden={!data?.tnHidden}
          title="Enable Talent Network"
          description="Let job board visitors join your talent network and express interest in working with your companies."
        />
        {!isLoading && !data?.tnHidden && (
          <>
            <TalentDivider />
            <TalentNetworkSettings
              isUpdating={isUpdatingTalentNetwork || isUpdatingJobBoardSettings}
              update={(values) => {
                updateTalentNetwork(values).then(() => {
                  toast('Changes saved successfully', { icon: 'success' });
                });
              }}
              talentNetwork={data}
              error={isError || isErrorJobBoardSettings ? strings.genericError : ''}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
